import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Body2,
  Button,
  Dialog,
  InfoBox,
  SkeletonLoader,
  StepActionButton,
  useWizardContext,
} from "@activecollab/components";
import { DragProvider, ReorderableList, reorderArray, useAutoScroll } from "@activecollab/yank-and-drop";
import classNames from "classnames";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import { DraggableSuggestionItem } from "./DraggableSuggestionItem";
import { NEW, SUGGESTION } from "./suggestionUtils";
import { VoteThumbs } from "../../ai/VoteThumbs";

const loadingBarsArr = [1, 2, 3];

const LoadingBars = () => (
  <div data-testid="add-tasks-step-loading">
    {loadingBarsArr.map((bar) => (
      <div
        key={bar}
        className="tw-flex tw-px-1.5 tw-py-3"
      >
        <SkeletonLoader className="tw-flex-1" />
        <SkeletonLoader className="tw-w-20" />
        <SkeletonLoader className="tw-w-6 tw-mx-4" />
        <SkeletonLoader className="tw-w-6" />
      </div>
    ))}
  </div>
);

export const AddSuggestionsStep = ({
  loading,
  error,
  suggestions,
  promptId,
  projectId,
  infoBoxMessage,
  buttonText,
  submitting,
  onSubmit,
  selectionMode,
  shouldShowEstimate,
}) => {
  const { t } = useTranslation();
  const { goPrevious } = useWizardContext();
  const scrollContainerRef = useRef(null);

  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!loading && suggestions.length > 0) {
      setItems(
        suggestions.map((suggestion) => ({
          id: v4(),
          name: suggestion,
          variant: SUGGESTION,
          assignee_id: 0,
          start_on: null,
          due_on: null,
          ...(shouldShowEstimate && { estimate: null, job_type_id: null }),
        }))
      );
    }
  }, [loading, shouldShowEstimate, suggestions]);

  useEffect(() => {
    if (error) {
      goPrevious();
    }
  }, [error, goPrevious]);

  const updateValue = useCallback((id, field, value) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  }, []);

  const handleRemove = useCallback((id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleReorder = useCallback((fromIndex, toIndex) => {
    setItems((currentItems) => reorderArray(currentItems, fromIndex, toIndex));
  }, []);

  const handleAddNew = () => {
    setItems((prev) => [
      ...prev,
      {
        id: v4(),
        name: "",
        assignee_id: 0,
        variant: NEW,
        start_on: null,
        due_on: null,
        ...(shouldShowEstimate && { estimate: null, job_type_id: null }),
      },
    ]);
  };

  useAutoScroll(scrollContainerRef.current);

  const handleOnSubmit = useCallback(() => {
    onSubmit(items);
  }, [items, onSubmit]);

  return (
    <>
      <Dialog.Content>
        {loading ? (
          <LoadingBars />
        ) : (
          <div>
            {!!suggestions.length && (
              <InfoBox
                className="tw-mb-6 tw-px-5 tw-pt-4 tw-pb-3"
                type="ai"
              >
                <Body2 className="tw-mb-3">{infoBoxMessage}</Body2>
                <VoteThumbs id={promptId} />
              </InfoBox>
            )}
            <div className={classNames({ "tw-pointer-events-none": submitting })}>
              <div
                className={classNames("tw-overflow-y-auto tw-overflow-x-hidden tw-max-h-64 tw-mx-[-24px] mb-4", {
                  "tw-min-h-[40px]": !!suggestions.length,
                })}
                ref={scrollContainerRef}
              >
                {!suggestions.length && (
                  <Body2 className="tw-mx-6">{t("Unable to suggest tasks. Please refine your input.")}</Body2>
                )}
                <DragProvider>
                  <ReorderableList
                    onMove={handleReorder}
                    dragAxis="y"
                    handle=".drag-handle"
                  >
                    {items.map((item) => (
                      <DraggableSuggestionItem
                        key={item.id}
                        id={item.id}
                        projectId={projectId}
                        task={item}
                        onRemove={handleRemove}
                        disabled={submitting}
                        item={item}
                        onUpdate={updateValue}
                        selectionMode={selectionMode}
                        shouldShowEstimate={shouldShowEstimate}
                      />
                    ))}
                  </ReorderableList>
                </DragProvider>
                {!!suggestions.length && (
                  <Button
                    className="tw-ml-6"
                    variant="tertiary"
                    onClick={handleAddNew}
                    disabled={loading || submitting}
                  >
                    {buttonText}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Dialog.Content>
      <>
        <Dialog.ContentDivider />
        <Dialog.Actions>
          <StepActionButton
            isLast
            disabled={loading || submitting || !items.length}
            onNextButtonClick={handleOnSubmit}
            actionLabel={t("Add Suggestions")}
          />
        </Dialog.Actions>
      </>
    </>
  );
};

AddSuggestionsStep.propTypes = {
  projectId: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  suggestions: PropTypes.arrayOf(PropTypes.string),
  promptId: PropTypes.number,
  infoBoxMessage: PropTypes.string,
  buttonText: PropTypes.string,
  onSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  selectionMode: PropTypes.string,
  shouldShowEstimate: PropTypes.bool,
};

AddSuggestionsStep.defaultProps = {
  suggestions: [],
  shouldShowEstimate: false,
};
